import React, { useEffect, useMemo, useRef, useState } from 'react';

// Messages shown during AI generation. Each has a delay (ms) before the next
// message appears: normal/informative ones stay longer, fun ones flash quicker.
const LOADING_MESSAGES = [
  { text: 'AI replacing texts…',                   delay: 8000 },
  { text: 'AI replacing images…',                  delay: 4000 },
  { text: 'Zoltar Demo Pilot doing its magic…',    delay: 3000 },
  { text: 'Matching brand voice…',                 delay: 3500 },
  { text: 'AI being more creative…',               delay: 1800 },
  { text: 'Finding the perfect images…',           delay: 3500 },
  { text: 'AI being more genuine…',                delay: 1800 },
  { text: 'Crafting compelling headlines…',         delay: 3500 },
  { text: 'Fine-tuning the copy…',                 delay: 3000 },
  { text: 'Almost there…',                         delay: 4000 },
];

function useRotatingMessage(active) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }
    setIndex(0);
    let step = 0;

    function scheduleNext() {
      const current = step < 2 ? step : 2 + Math.floor(Math.random() * (LOADING_MESSAGES.length - 2));
      const delay = LOADING_MESSAGES[current].delay;
      timerRef.current = setTimeout(() => {
        step++;
        const next = step < 2 ? step : 2 + Math.floor(Math.random() * (LOADING_MESSAGES.length - 2));
        setIndex(next);
        scheduleNext();
      }, delay);
    }
    scheduleNext();

    return () => clearTimeout(timerRef.current);
  }, [active]);

  return LOADING_MESSAGES[index]?.text || LOADING_MESSAGES[0].text;
}

export default function AIGenerateTab({
  texts,
  damFolderPath,
  damAssetCount,
  targetBrand,
  onTargetBrandChange,
  scrapeUrl,
  onScrapeUrlChange,
  pageType,
  onPageTypeChange,
  language,
  onLanguageChange,
  additionalContext,
  onAdditionalContextChange,
  onImportClick,
  onReplaceAllPage,
  replacePageStatus = { phase: 'idle', progress: { done: 0, total: 0 }, error: '' },
  onDiscoverPages,
  onReplaceSelectedPages,
  replaceAllPagesStatus = { phase: 'idle', currentPage: '', progress: { done: 0, total: 0 }, results: [], error: '' },
  discoveredPages = [],
  selectedPagePaths = new Set(),
  onSelectedPagePathsChange,
  walHistory = { head: 0, maxSeq: 0, steps: [], loaded: false },
  walBusy = false,
  onRefreshHistory,
  onRequestRestore,
}) {
  const textStats = useMemo(() => {
    const categories = Object.keys(texts || {});
    let total = 0;
    for (const cat of categories) {
      total += (texts[cat] || []).length;
    }
    return { categories: categories.length, total };
  }, [texts]);

  const hasScrapedContent = textStats.total > 0;
  const [contextOpen, setContextOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  // Short, human-friendly label for a JCR page path (last 1-2 segments).
  const pageLabel = (path) => {
    if (!path) return '';
    const segs = String(path).split('/').filter(Boolean);
    return segs.slice(-2).join('/') || path;
  };

  // Fold the flat WAL steps into display rows: consecutive steps sharing a
  // groupId (one whole-site rebrand job) collapse into a single expandable
  // group; ungrouped steps (single-page rebrands) render on their own.
  const historyRows = useMemo(() => {
    const rows = [];
    const byGroup = {};
    for (const s of walHistory.steps || []) {
      if (s.groupId) {
        if (!byGroup[s.groupId]) {
          const row = { type: 'group', groupId: s.groupId, name: s.name, steps: [] };
          byGroup[s.groupId] = row;
          rows.push(row);
        }
        byGroup[s.groupId].steps.push(s);
      } else {
        rows.push({ type: 'step', step: s });
      }
    }
    for (const r of rows) {
      if (r.type !== 'group') continue;
      r.minSeq = r.steps[0].seq;
      r.maxSeq = r.steps[r.steps.length - 1].seq;
      r.totalPages = r.steps.reduce((n, s) => n + (s.pages || 0), 0);
      r.totalChanges = r.steps.reduce((n, s) => n + (s.entries || 0), 0);
    }
    return rows;
  }, [walHistory.steps]);

  const replacePageBusy = replacePageStatus.phase !== 'idle' && replacePageStatus.phase !== 'done' && replacePageStatus.phase !== 'error';
  const replaceAllPagesBusy = replaceAllPagesStatus.phase === 'listing' || replaceAllPagesStatus.phase === 'replacing';
  const anyBusy = replacePageBusy || replaceAllPagesBusy;
  const canGenerate = (hasScrapedContent || targetBrand.trim()) && !anyBusy;
  const canGenerateAll = (hasScrapedContent || targetBrand.trim()) && !anyBusy;

  const rotatingMsg = useRotatingMessage(replacePageStatus.phase === 'generating' || replaceAllPagesStatus.phase === 'replacing');

  const replacePageLabel = () => {
    const { phase, progress } = replacePageStatus;
    if (phase === 'collecting') return `Scanning page… (${progress.done}/${progress.total})`;
    if (phase === 'fetching-dam') return 'Loading DAM assets…';
    if (phase === 'generating') return rotatingMsg;
    if (phase === 'applying') return `Applying… (${progress.done}/${progress.total})`;
    if (phase === 'done') return `Done! (${progress.done} replaced)`;
    return 'Rebrand Page with AI';
  };

  const isSelecting = replaceAllPagesStatus.phase === 'selecting';
  const isMultiPageActive = replaceAllPagesStatus.phase === 'listing'
    || replaceAllPagesStatus.phase === 'selecting'
    || replaceAllPagesStatus.phase === 'replacing';
  const selectedCount = selectedPagePaths.size;

  // Auto-fold AI context when the multi-page flow starts.
  useEffect(() => {
    if (isMultiPageActive) setContextOpen(false);
  }, [isMultiPageActive]);

  const togglePage = (path) => {
    const next = new Set(selectedPagePaths);
    if (next.has(path)) next.delete(path); else next.add(path);
    onSelectedPagePathsChange(next);
  };
  const selectAll = () => onSelectedPagePathsChange(new Set(discoveredPages.map((p) => p.path)));
  const deselectAll = () => onSelectedPagePathsChange(new Set());

  const replaceAllPagesLabel = () => {
    const { phase, progress } = replaceAllPagesStatus;
    if (phase === 'listing') return 'Discovering site pages…';
    if (phase === 'replacing') return `${rotatingMsg} (${progress.done}/${progress.total})`;
    if (phase === 'done') {
      const totalApplied = replaceAllPagesStatus.results.reduce((s, r) => s + r.applied, 0);
      return `Done! ${totalApplied} replacements across ${progress.total} pages`;
    }
    return 'Discover Site Pages';
  };

  const damLabel = damAssetCount !== null ? String(damAssetCount) : '—';

  // Render one WAL step as a timeline item. `child` = nested inside a group
  // (a single page of a whole-site rebrand): show its page instead of the brand.
  const renderStep = (s, { child = false } = {}) => {
    const isCurrent = s.seq === walHistory.head;
    const isFuture = s.seq > walHistory.head;
    const pages = s.pagePaths && s.pagePaths.length
      ? s.pagePaths.map(pageLabel).join(', ')
      : `${s.pages} page${s.pages !== 1 ? 's' : ''}`;
    const changes = `${s.entries} change${s.entries !== 1 ? 's' : ''}`;
    return (
      <li key={s.seq} className={`dp-history-item ${child ? 'dp-history-child' : ''} ${isCurrent ? 'is-current' : ''} ${isFuture ? 'is-future' : ''}`}>
        <span className="dp-history-marker" aria-hidden>{isCurrent ? '●' : '○'}</span>
        <span className="dp-history-info">
          <span className="dp-history-name">{child ? pages : `#${s.seq} ${s.name}`}</span>
          <span className="dp-history-meta">
            {child ? changes : `${pages} · ${changes}`}{isCurrent ? ' · current' : ''}
          </span>
        </span>
        {!isCurrent && onRequestRestore && (
          <button
            type="button"
            className="dp-history-btn"
            disabled={walBusy}
            onClick={() => onRequestRestore(s.seq, s.name, isFuture ? 'redo' : 'undo')}
          >
            {isFuture ? '↪ Redo' : '↩ Revert'}
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="dp-tab-content">
      {/* Change history (WAL) — collapsible timeline with revert / redo */}
      {(walHistory.steps.length > 0 || walBusy) && (
        <div className="dp-gen-context dp-history">
          <button
            type="button"
            className="dp-gen-context-toggle"
            onClick={() => { setHistoryOpen((v) => !v); if (!historyOpen && onRefreshHistory) onRefreshHistory(); }}
            aria-expanded={historyOpen}
          >
            <span className={`dp-chevron ${historyOpen ? 'is-open' : ''}`}>›</span>
            <span className="dp-gen-context-title">
              Change history
              {walHistory.steps.length > 0 && <span className="dp-gen-context-optional">{walHistory.steps.length} step{walHistory.steps.length !== 1 ? 's' : ''}</span>}
            </span>
          </button>

          {historyOpen && (
            <div className="dp-gen-context-body">
              {walBusy && <div className="dp-history-loading"><span className="dp-spinner" aria-hidden /> Working…</div>}

              <ul className="dp-history-list">
                {[...historyRows].reverse().map((r) => {
                  if (r.type === 'step') return renderStep(r.step);

                  // Whole-site rebrand: collapse its per-page steps under one entry.
                  const open = !!openGroups[r.groupId];
                  const fullyUndone = walHistory.head < r.minSeq;
                  const groupDir = fullyUndone ? 'redo' : 'undo';
                  const groupTarget = fullyUndone ? r.maxSeq : r.minSeq - 1;
                  const groupIsCurrent = walHistory.head === r.maxSeq;
                  return (
                    <li key={r.groupId} className={`dp-history-group ${groupIsCurrent ? 'is-current' : ''}`}>
                      <div className="dp-history-item dp-history-group-head">
                        <button
                          type="button"
                          className="dp-history-grouptoggle"
                          onClick={() => setOpenGroups((g) => ({ ...g, [r.groupId]: !g[r.groupId] }))}
                          aria-expanded={open}
                        >
                          <span className={`dp-chevron ${open ? 'is-open' : ''}`}>›</span>
                          <span className="dp-history-marker" aria-hidden>{groupIsCurrent ? '●' : '○'}</span>
                          <span className="dp-history-info">
                            <span className="dp-history-name">{r.name} <span className="dp-history-badge">site</span></span>
                            <span className="dp-history-meta">
                              {r.totalPages} page{r.totalPages !== 1 ? 's' : ''} · {r.totalChanges} change{r.totalChanges !== 1 ? 's' : ''}
                              {groupIsCurrent ? ' · current' : ''}
                            </span>
                          </span>
                        </button>
                        {onRequestRestore && (
                          <button
                            type="button"
                            className="dp-history-btn"
                            disabled={walBusy}
                            onClick={() => onRequestRestore(groupTarget, r.name, groupDir)}
                          >
                            {groupDir === 'redo' ? '↪ Redo all' : '↩ Revert all'}
                          </button>
                        )}
                      </div>
                      {open && (
                        <ul className="dp-history-sublist">
                          {[...r.steps].reverse().map((s) => renderStep(s, { child: true }))}
                        </ul>
                      )}
                    </li>
                  );
                })}

                {/* Original content (before any step) */}
                <li className={`dp-history-item dp-history-initial ${walHistory.head === 0 ? 'is-current' : ''}`}>
                  <span className="dp-history-marker" aria-hidden>{walHistory.head === 0 ? '●' : '○'}</span>
                  <span className="dp-history-info">
                    <span className="dp-history-name">Original content</span>
                    <span className="dp-history-meta">before any rebrand{walHistory.head === 0 ? ' · current' : ''}</span>
                  </span>
                  {walHistory.head > 0 && onRequestRestore && (
                    <button
                      type="button"
                      className="dp-history-btn"
                      disabled={walBusy}
                      onClick={() => onRequestRestore(0, 'initial', 'undo')}
                    >
                      ↩ Revert
                    </button>
                  )}
                </li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="dp-gen-stats">
        <div className="dp-gen-stat-card">
          <span className="dp-gen-stat-value">{textStats.total}</span>
          <span className="dp-gen-stat-label">Texts</span>
          {textStats.categories > 0 && (
            <span className="dp-gen-stat-detail">{textStats.categories} categories</span>
          )}
        </div>
        <div className="dp-gen-stat-card">
          <span className="dp-gen-stat-value">{damLabel}</span>
          <span className="dp-gen-stat-label">DAM Assets</span>
          {damAssetCount === null && (
            <span className="dp-gen-stat-detail dp-gen-stat-loading">loading…</span>
          )}
        </div>
      </div>

      {!hasScrapedContent && (
        <div className="dp-gen-notice dp-gen-notice--warn">
          <span className="dp-gen-notice-icon" aria-hidden>&#9888;</span>
          <div>
            <strong>No content scraped yet.</strong>
            <p>Import content from a reference website, or enter a target brand below so the AI has context to generate from.</p>
          </div>
        </div>
      )}

      <button className="dp-btn dp-btn-ghost dp-btn-full" onClick={onImportClick}>
        + Import / Scrape content
      </button>

      {/* AI Context — always present, auto-folds when multi-page flow starts */}
      <div className="dp-gen-context">
        <button
          type="button"
          className="dp-gen-context-toggle"
          onClick={() => setContextOpen((v) => !v)}
          aria-expanded={contextOpen}
        >
          <span className={`dp-chevron ${contextOpen ? 'is-open' : ''}`}>›</span>
          <span className="dp-gen-context-title">
            AI Context
            {!hasScrapedContent && <span className="dp-gen-context-required">required</span>}
            {hasScrapedContent && <span className="dp-gen-context-optional">optional</span>}
          </span>
        </button>

        {contextOpen && (
          <div className="dp-gen-context-body">
            <label className="dp-gen-field">
              <span className="dp-gen-field-label">
                Brand name
                {!hasScrapedContent && <span className="dp-gen-field-req"> *</span>}
              </span>
              <input
                type="text"
                className={`dp-gen-field-input ${!hasScrapedContent && !targetBrand.trim() ? 'is-required' : ''}`}
                placeholder="e.g. WKND Adventures"
                value={targetBrand}
                onChange={(e) => onTargetBrandChange(e.target.value)}
              />
              {!hasScrapedContent && !targetBrand.trim() && (
                <span className="dp-gen-field-hint">Provide a brand name so the AI knows what content to generate.</span>
              )}
            </label>

            <label className="dp-gen-field">
              <span className="dp-gen-field-label">
                Reference website URL
                <span className="dp-gen-context-optional" style={{ marginLeft: 4 }}>for tone &amp; brand context</span>
              </span>
              <input
                type="url"
                className="dp-gen-field-input"
                placeholder="https://www.client-brand.com"
                value={scrapeUrl || ''}
                onChange={(e) => onScrapeUrlChange(e.target.value)}
              />
              <span className="dp-gen-field-hint">Scrapes tone of voice, brand guidelines and page description to enrich the AI prompt.</span>
            </label>

            <label className="dp-gen-field">
              <span className="dp-gen-field-label">Page type</span>
              <input
                type="text"
                className="dp-gen-field-input"
                placeholder="e.g. Homepage, Product page, Blog article"
                value={pageType}
                onChange={(e) => onPageTypeChange(e.target.value)}
              />
            </label>

            <label className="dp-gen-field">
              <span className="dp-gen-field-label">Select Language</span>
              <select
                className="dp-gen-field-input"
                value={language}
                onChange={(e) => onLanguageChange(e.target.value)}
              >
                <option value="">Keep page language</option>
                <option value="English">English</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Spanish">Spanish</option>
                <option value="Italian">Italian</option>
                <option value="Portuguese">Portuguese</option>
                <option value="Dutch">Dutch</option>
                <option value="Japanese">Japanese</option>
                <option value="Korean">Korean</option>
                <option value="Chinese (Simplified)">Chinese (Simplified)</option>
                <option value="Arabic">Arabic</option>
              </select>
            </label>

            <label className="dp-gen-field">
              <span className="dp-gen-field-label">Additional instructions</span>
              <textarea
                className="dp-gen-field-textarea"
                placeholder="e.g. Use a friendly tone, focus on outdoor activities…"
                value={additionalContext}
                onChange={(e) => onAdditionalContextChange(e.target.value)}
                rows={2}
              />
            </label>
          </div>
        )}
      </div>

      {/* Single-page replace: hidden during multi-page flow */}
      {!isMultiPageActive && (
        <>
          <button
            className={`dp-btn dp-btn-full dp-btn-ai-page ${replacePageBusy ? 'is-busy' : ''} ${replacePageStatus.phase === 'done' ? 'is-done' : ''}`}
            onClick={onReplaceAllPage}
            disabled={!canGenerate}
            title={!canGenerate && !replacePageBusy ? 'Scrape content or enter a target brand first' : ''}
          >
            {replacePageBusy && <span className="dp-spinner dp-spinner-ai" aria-hidden />}
            <span>{replacePageLabel()}</span>
          </button>

          {replacePageStatus.phase === 'error' && replacePageStatus.error && (
            <div className="dp-status is-error">
              <span>{replacePageStatus.error}</span>
            </div>
          )}

          <div className="dp-gen-separator" />
        </>
      )}

      {/* Step 1: Discover pages button (only when not yet selecting or replacing) */}
      {!isSelecting && replaceAllPagesStatus.phase !== 'replacing' && (
        <button
          className={`dp-btn dp-btn-full dp-btn-ai-all-pages ${replaceAllPagesStatus.phase === 'listing' ? 'is-busy' : ''} ${replaceAllPagesStatus.phase === 'done' ? 'is-done' : ''}`}
          onClick={onDiscoverPages}
          disabled={!canGenerateAll || replaceAllPagesStatus.phase === 'listing'}
          title={!canGenerateAll && !replaceAllPagesBusy ? 'Scrape content or enter a target brand first' : ''}
        >
          {replaceAllPagesStatus.phase === 'listing' && <span className="dp-spinner dp-spinner-ai" aria-hidden />}
          <span>{replaceAllPagesLabel()}</span>
        </button>
      )}

      {/* Step 2: Page selection list */}
      {isSelecting && discoveredPages.length > 0 && (
        <div className="dp-page-selector">
          <div className="dp-page-selector-header">
            <span className="dp-page-selector-title">{discoveredPages.length} pages found</span>
            <div className="dp-page-selector-actions">
              <button type="button" className="dp-page-selector-link" onClick={selectAll}>All</button>
              <span className="dp-page-selector-sep">/</span>
              <button type="button" className="dp-page-selector-link" onClick={deselectAll}>None</button>
            </div>
          </div>
          <div className="dp-page-selector-list">
            {discoveredPages.map((p) => {
              const checked = selectedPagePaths.has(p.path);
              const indent = p.depth || 0;
              return (
                <label
                  key={p.path}
                  className={`dp-page-selector-item ${checked ? 'is-checked' : ''} ${p.isNav || p.isFooter ? 'is-structural' : ''}`}
                  style={indent > 0 ? { paddingLeft: `${12 + indent * 16}px` } : undefined}
                >
                  <input
                    type="checkbox"
                    className="dp-page-selector-cb"
                    checked={checked}
                    onChange={() => togglePage(p.path)}
                  />
                  <span className="dp-page-selector-name">{p.title || p.name}</span>
                  <span className="dp-page-selector-slug">{p.name}</span>
                  {(p.isNav || p.isFooter) && <span className="dp-page-selector-badge">structural</span>}
                </label>
              );
            })}
          </div>
          <button
            className={`dp-btn dp-btn-full dp-btn-ai-all-pages ${selectedCount === 0 ? '' : ''}`}
            onClick={selectedCount > 0 ? onReplaceSelectedPages : undefined}
            disabled={selectedCount === 0}
          >
            <span>Replace {selectedCount} page{selectedCount !== 1 ? 's' : ''} with AI</span>
          </button>
        </div>
      )}

      {/* Progress bar during replacement */}
      {replaceAllPagesStatus.phase === 'replacing' && replaceAllPagesStatus.currentPage && (
        <div className="dp-gen-all-pages-progress">
          <div className="dp-gen-all-pages-bar-track">
            <div
              className="dp-gen-all-pages-bar-fill"
              style={{ width: `${replaceAllPagesStatus.progress.total > 0 ? (replaceAllPagesStatus.progress.done / replaceAllPagesStatus.progress.total) * 100 : 0}%` }}
            />
          </div>
          <span className="dp-gen-all-pages-current">
            {replaceAllPagesStatus.progress.done}/{replaceAllPagesStatus.progress.total} — {replaceAllPagesStatus.currentPage}
          </span>
        </div>
      )}

      {/* Results list after completion */}
      {replaceAllPagesStatus.phase === 'done' && replaceAllPagesStatus.results.length > 0 && (
        <div className="dp-gen-all-pages-results">
          {replaceAllPagesStatus.results.map((r, i) => (
            <div key={i} className={`dp-gen-all-pages-result ${r.ok ? 'is-ok' : 'is-error'}`}>
              <span className="dp-gen-all-pages-result-icon">{r.ok ? '\u2713' : '\u2717'}</span>
              <span className="dp-gen-all-pages-result-name">{r.title || r.page}</span>
              <span className="dp-gen-all-pages-result-detail">
                {r.ok ? `${r.applied} replaced` : r.error}
              </span>
            </div>
          ))}
        </div>
      )}

      {replaceAllPagesStatus.phase === 'error' && replaceAllPagesStatus.error && (
        <div className="dp-status is-error">
          <span>{replaceAllPagesStatus.error}</span>
        </div>
      )}
    </div>
  );
}
